import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ExternalLink, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { BASE_URL, apiMethods } from "@/utils/apis";

// This component will display the integrations section on the dashboard
const IntegrationsSection = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({
    github: { connected: false, isChecking: true },
    notion: { connected: false, isChecking: true }
  });

  // Check GitHub and Notion access immediately on component load
  useEffect(() => {
    // Run both checks in parallel
    Promise.all([
      checkGithubAccess(),
      checkNotionAccess()
    ]);
    
    // Try to use cached values from localStorage while checking
    const cachedGithubStatus = localStorage.getItem('github_connected') === 'true';
    const cachedNotionStatus = localStorage.getItem('notion_connected') === 'true';
    
    if (cachedGithubStatus || cachedNotionStatus) {
      setConnectionStatus(prev => ({
        github: { ...prev.github, connected: cachedGithubStatus },
        notion: { ...prev.notion, connected: cachedNotionStatus }
      }));
    }
  }, []);

  // Function to check if the user has GitHub access
  const checkGithubAccess = useCallback(async () => {
    try {
      const response = await apiMethods.checkGithubAccess();
      const isConnected = response.has_access;
      
      // Cache the result
      localStorage.setItem('github_connected', isConnected ? 'true' : 'false');
      
      setConnectionStatus(prev => ({
        ...prev,
        github: { connected: isConnected, isChecking: false }
      }));
    } catch (error) {
      console.error("Error checking GitHub access:", error);
      setConnectionStatus(prev => ({
        ...prev,
        github: { connected: false, isChecking: false }
      }));
    }
  }, []);

  // Function to check if the user has Notion access
  const checkNotionAccess = useCallback(async () => {
    try {
      const response = await apiMethods.notionCheckToken();
      const isConnected = (response?.status === "valid") || false;
      
      // Cache the result
      localStorage.setItem('notion_connected', isConnected ? 'true' : 'false');
      
      setConnectionStatus(prev => ({
        ...prev,
        notion: { connected: isConnected, isChecking: false }
      }));
    } catch (error) {
      console.error("Error checking Notion access:", error);
      setConnectionStatus(prev => ({
        ...prev,
        notion: { connected: false, isChecking: false }
      }));
    }
  }, []);

  // Handle connecting to GitHub via OAuth
  const handleGithubConnect = () => {
    setGithubLoading(true);
    try {
      // Store current URL for redirect back after GitHub auth
      localStorage.setItem("github_redirect_url", window.location.pathname);
      // Redirect to GitHub authorization endpoint
      window.location.href = `${BASE_URL}/github/authorize-app`;
      // Don't set loading=false as we're navigating away
    } catch (error) {
      console.error("Error connecting to GitHub:", error);
      toast.error("Failed to connect to GitHub. Please try again.");
      setGithubLoading(false);
    }
  };

  // Handle connecting to Notion via OAuth
  const handleNotionConnect = () => {
    setIsLoading(true);
    try {
      // Get the user token from localStorage
      const userToken = localStorage.getItem('token') || '';
      
      // With the updated API, we can simply redirect to the authorize endpoint
      // The backend will handle the OAuth flow and redirect to success/error pages
      window.location.href = `${BASE_URL}/integrations/notion/authorize?token=${userToken}`;
      
      // Don't set isLoading=false as we're navigating away
    } catch (error) {
      console.error("Error connecting to Notion:", error);
      toast.error("Failed to connect to Notion. Please try again.");
      setIsLoading(false);
    }
  };

  // Define upcoming integration icons and names
  const upcomingIntegrations = [
    {
      name: "Confluence",
      icon: "https://cdn.worldvectorlogo.com/logos/confluence-1.svg",
      description: "Confluence export"
    },
    {
      name: "Jira",
      icon: "https://cdn.worldvectorlogo.com/logos/jira-1.svg",
      description: "Jira integration"
    },
    {
      name: "Bitbucket",
      icon: "https://cdn.worldvectorlogo.com/logos/bitbucket-icon.svg",
      description: "Bitbucket integration"
    },
    {
      name: "GitLab",
      icon: "https://cdn.worldvectorlogo.com/logos/gitlab.svg",
      description: "GitLab integration"
    },
    {
      name: "Slack",
      icon: "https://cdn.worldvectorlogo.com/logos/slack-new-logo.svg",
      description: "Custom webhook support"
    },
    {
      name: "Tableau",
      icon: "https://cdn.worldvectorlogo.com/logos/tableau-software.svg",
      description: "Data visualization"
    }
  ];

  return (
    <div className="p-8 bg-white dark:bg-[#0d1117]">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Integrations</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Connect DocGen with other services to enhance your documentation workflow.
        </p>
      </div>

      {/* Active Integrations Section */}
      <div className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Active Integrations</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* GitHub Integration Tile */}
          <div className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden bg-gray-100/80 dark:bg-gray-800/30 hover:bg-gray-200/80 dark:hover:bg-gray-800/50 transition-colors relative flex flex-col h-[200px]">
            {connectionStatus.github.connected && (
              <div className="absolute top-2 right-2 bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400 text-xs font-medium px-2 py-0.5 rounded-full flex items-center">
                <Check className="h-3 w-3 mr-1" />
                Connected
              </div>
            )}
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-center mb-3">
                <div className="bg-white p-2 rounded-md mr-3">
                  <img 
                    src="/github-logo.png" 
                    alt="GitHub Logo" 
                    className="w-8 h-8"
                    onError={(e) => {
                      e.currentTarget.src = "https://github.githubassets.com/assets/GitHub-Mark-ea2971cee799.png";
                    }}
                  />
                </div>
                <div>
                  <h3 className="text-gray-900 dark:text-white font-medium">GitHub</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Code repositories</p>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                {connectionStatus.github.connected 
                  ? "Import repositories directly from GitHub" 
                  : "Import repositories into your projects"}
              </p>
              
              <div className="mt-auto">
                <Button 
                  onClick={handleGithubConnect}
                  disabled={githubLoading}
                  className="w-full bg-white text-black hover:bg-white/90 text-sm dark:border-gray-700"
                >
                  <span className="flex items-center justify-center">
                    {connectionStatus.github.connected ? "Reconnect" : "Connect"}
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </span>
                </Button>
              </div>
            </div>
          </div>

          {/* Notion Integration Tile */}
          <div className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden bg-gray-100/80 dark:bg-gray-800/30 hover:bg-gray-200/80 dark:hover:bg-gray-800/50 transition-colors relative flex flex-col h-[200px]">
            {connectionStatus.notion.connected && (
              <div className="absolute top-2 right-2 bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400 text-xs font-medium px-2 py-0.5 rounded-full flex items-center">
                <Check className="h-3 w-3 mr-1" />
                Connected
              </div>
            )}
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-center mb-3">
                <div className="bg-white p-2 rounded-md mr-3">
                  <img 
                    src="/notion-logo.png" 
                    alt="Notion Logo" 
                    className="w-8 h-8"
                    onError={(e) => {
                      e.currentTarget.src = "https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png";
                    }}
                  />
                </div>
                <div>
                  <h3 className="text-gray-900 dark:text-white font-medium">Notion</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Workspace docs</p>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                {connectionStatus.notion.connected 
                  ? "Export documentation to your Notion workspace" 
                  : "Export docs to your Notion workspace"}
              </p>
              
              <div className="mt-auto">
                <Button 
                  onClick={handleNotionConnect}
                  disabled={isLoading}
                  className="w-full bg-white text-black hover:bg-white/90 text-sm dark:border-gray-700"
                >
                  <span className="flex items-center justify-center">
                    {connectionStatus.notion.connected ? "Reconnect" : "Connect"}
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* More Coming Soon Section - Updated heading text color */}
      <div className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        <div className="px-6 py-5 flex items-center">
          <Plus className="h-5 w-5 text-gray-700 dark:text-gray-500 mr-3" />
          <div className="flex-1">
            <div className="flex items-center">
              <h2 className="text-xl font-semibold text-black dark:text-white">More Coming Soon</h2>
              <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded-full">Coming Soon</span>
            </div>
          </div>
        </div>
        
        <div className="pt-2 px-6 pb-6 border-t border-gray-200/50 dark:border-gray-800/50">
          <p className="text-gray-800 dark:text-gray-400 mb-4">
            We're working on adding more integrations. Stay tuned!
          </p>
          
          {/* Integration icons grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {upcomingIntegrations.map((integration, index) => (
              <div key={index} className="flex flex-col items-center bg-gray-100/80 dark:bg-gray-800/30 rounded-lg p-4 transition-all duration-200 hover:bg-gray-200/80 dark:hover:bg-gray-800/50">
                <div className="bg-white p-2 rounded-md mb-3 w-12 h-12 flex items-center justify-center">
                  <img
                    src={integration.icon}
                    alt={`${integration.name} Logo`}
                    className="w-8 h-8 object-contain"
                    onError={(e) => {
                      console.error(`Failed to load icon for ${integration.name}`);
                      e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23ddd'/%3E%3C/svg%3E";
                    }}
                  />
                </div>
                <span className="text-gray-900 dark:text-gray-300 text-sm font-medium">{integration.name}</span>
              </div>
            ))}
          </div>
          
          <div className="space-y-2">
            {upcomingIntegrations.map((integration, index) => (
              <div key={`check-${index}`} className="flex items-start">
                <div className="text-gray-700 dark:text-gray-500 mt-0.5 mr-2">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-gray-800 dark:text-gray-400">{integration.description}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-gray-200 dark:bg-gray-800/50 px-6 py-3 flex items-center text-gray-700 dark:text-gray-400 text-sm">
          <span className="flex-1">In development</span>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsSection; 